'use client';

import { type ReactNode, useEffect, useMemo, useState } from 'react';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import ModalBase from '../../../../components/ui/ModalBase/ModalBase';
import type { VariantType } from '../../../../types/variant';
import { withBlankLinks } from '../../utils';
import styles from './RequiredConsentModal.module.scss';

export type RequiredConsentModalProps = {
  isOpen: boolean;
  /** モーダル見出し。例: プライバシーポリシー改定のお知らせ */
  title: string;
  /**
   * 見出し下のリード文。改定の経緯や同意が必要な理由を記載する。
   * 文書ページへのリンクを含むため文字列ではなく ReactNode で受け取る。
   */
  leadText?: ReactNode;
  /** 規約本文。管理画面で登録された HTML をそのまま表示する */
  termHtml: string;
  /** チェックボックスの文言。例: プライバシーポリシーに同意する */
  checkboxLabel: string;
  agreeButtonLabel?: string;
  cancelButtonLabel?: string;
  /** 同意ボタン押下時。同意ログの登録は呼び出し側で行う */
  onAgree: () => void;
  /** キャンセルボタン押下時。拒否は記録せず GA イベントのみ発火する想定 */
  onCancel: () => void;
  /** 同意登録の通信中。ボタンをローディング表示にする */
  isSubmitting?: boolean;
  variant?: VariantType;
};

/**
 * 文書 再承諾モーダル。
 * 1文書＝1モーダルで本文をスクロール表示し、チェックボックスで同意の意思表示を受け取る。
 * 複数文書が同時に要再承諾の場合は、呼び出し側が本コンポーネントを順に表示する。
 */
function RequiredConsentModal({
  isOpen,
  title,
  leadText,
  termHtml,
  checkboxLabel,
  agreeButtonLabel = '同意してはじめる',
  cancelButtonLabel = 'キャンセル',
  onAgree,
  onCancel,
  isSubmitting = false,
  variant = 'default',
}: RequiredConsentModalProps) {
  const [isChecked, setIsChecked] = useState(false);

  // 管理画面のリッチテキスト入力ではリンクに別タブ指定ができないため、描画前に補う。
  // 同一タブで遷移するとモーダルが閉じ、同意も拒否もできないまま画面を離れてしまう
  const blankLinkedTermHtml = useMemo(() => withBlankLinks(termHtml), [termHtml]);

  // 連続表示で次の文書に切り替わった際にチェック状態を持ち越さない
  useEffect(() => {
    if (!isOpen) setIsChecked(false);
  }, [isOpen]);

  return (
    <div className={styles.requiredConsentModal}>
      {/* 同意/キャンセルの意思表示を必ず受け取るため、閉じるボタンは表示しない */}
      <ModalBase isOpen={isOpen} onClose={onCancel} showCloseButton={false}>
        <div className={styles.container}>
          {/* 装飾目的のアイコンなのでスクリーンリーダーには読み上げさせない */}
          <div className={styles.iconWrapper} aria-hidden='true'>
            <span className={styles.icon}>!</span>
          </div>

          <h2 className={styles.title}>{title}</h2>

          {leadText && <p className={styles.leadText}>{leadText}</p>}

          <div className={styles.termBody}>
            <div
              className={styles.termBodyInner}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: 管理画面で登録された信頼できるHTMLを表示するため
              dangerouslySetInnerHTML={{ __html: blankLinkedTermHtml }}
            />
          </div>

          <label className={styles.checkboxLabel}>
            <input
              type='checkbox'
              name='requiredConsent'
              className={styles.checkbox}
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
            />
            {checkboxLabel}
          </label>

          <div className={styles.buttons}>
            <ActionButton
              color='primary'
              size='small'
              variant={variant}
              disabled={!isChecked}
              isLoading={isSubmitting}
              onClick={onAgree}
            >
              {agreeButtonLabel}
            </ActionButton>
            {/* ActionButton は文字色が白固定のため、コントラストを確保できる warmgray を使う */}
            <ActionButton color='warmgray' size='small' variant={variant} disabled={isSubmitting} onClick={onCancel}>
              {cancelButtonLabel}
            </ActionButton>
          </div>
        </div>
      </ModalBase>
    </div>
  );
}

export default RequiredConsentModal;
